import MicroLabel from "@/components/base/MicroLabel";
import Tag from "@/components/base/Tag";

type Term = {
  term: string;
  category: string;
  definition: string;
};

interface TermPanelProps {
  term: Term;
  position: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** Whether a previous / next entry exists in the list currently on screen. */
  canPrev?: boolean;
  canNext?: boolean;
  variant?: "panel" | "inline";
}

/** One entry, rendered either as the big sticky reader or as the
    inline expansion used on narrow screens. Same copy, same order. */
export default function TermPanel({
  term,
  position,
  total,
  onPrev,
  onNext,
  canPrev,
  canNext,
  variant = "panel",
}: TermPanelProps) {
  const counter = `${String(position + 1).padStart(2, "0")} / ${total}`;

  /* Fall back to the full-list bounds only when the caller does not know
     which neighbours exist in the filtered list. */
  const prevEnabled = canPrev ?? position > 0;
  const nextEnabled = canNext ?? position < total - 1;

  if (variant === "inline") {
    return (
      <div className="border-t-2 border-foreground-950 bg-background-100 px-4 py-5">
        <div className="flex items-center justify-between gap-3">
          <MicroLabel mark="dot" tone="muted">
            {counter}
          </MicroLabel>
          <Tag tone="accent">{term.category}</Tag>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-foreground-700">{term.definition}</p>
      </div>
    );
  }

  return (
    <div className="border-2 border-foreground-950 bg-background-50">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b-2 border-foreground-950 bg-background-100">
        <MicroLabel mark="dot">Definition {counter}</MicroLabel>
        <Tag tone="accent">{term.category}</Tag>
      </div>

      <div className="px-5 md:px-8 py-8 md:py-12">
        <h3 className="font-heading font-black uppercase leading-[0.95] tracking-tight text-foreground-950 text-3xl md:text-5xl">
          {term.term}
        </h3>
        <span className="mt-5 block h-1 w-16 bg-accent-500" aria-hidden="true" />
        <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-foreground-700">
          {term.definition}
        </p>
      </div>

      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t-2 border-foreground-950">
        <button
          type="button"
          onClick={onPrev}
          disabled={!prevEnabled}
          className="inline-flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.18em] font-semibold text-foreground-950 hover:text-primary-700 disabled:text-foreground-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          Previous
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!nextEnabled}
          className="inline-flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.18em] font-semibold text-foreground-950 hover:text-primary-700 disabled:text-foreground-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
        >
          Next
          <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  );
}